import React from 'react'
import { observer } from 'mobx-react-lite'
import { startOfMonth } from 'date-fns/startOfMonth'
import { endOfMonth } from 'date-fns/endOfMonth'

import {
  Loader,
  MiniWidgetContainer,
  useAccountStore,
  useCategoryStore,
  useGlobalCopyStore,
  useGlobalUiStore,
  useWidgetLoadTimer,
} from '../common'
import { MiniWidgetProps } from '../core'

import MiniSpendingChart from './components/MiniSpendingChart'

const SpendingMiniWidget: React.FC<MiniWidgetProps> = ({ onPrimaryCtaClick, sx = {} }) => {
  const { isAccountDataLoaded, loadAccountData } = useAccountStore()
  const { dateRangeTotalsLoaded, categoriesLoaded, loadCategories, loadDateRangeCategoryTotals } =
    useCategoryStore()
  const { spending: spendingCopy } = useGlobalCopyStore()
  const { isInitialized, selectedAccounts } = useGlobalUiStore()

  useWidgetLoadTimer({
    widgetName: 'SpendingMiniWidget',
    isLoaded: dateRangeTotalsLoaded,
  })

  React.useEffect(() => {
    if (!isAccountDataLoaded) {
      loadAccountData().finally()
    }
    if (!categoriesLoaded) {
      loadCategories().finally()
    }
  }, [])

  React.useEffect(() => {
    if (!isInitialized || !isAccountDataLoaded) return

    loadDateRangeCategoryTotals(
      selectedAccounts,
      startOfMonth(new Date()),
      endOfMonth(new Date()),
    ).finally()
  }, [isInitialized, isAccountDataLoaded, selectedAccounts])

  // Keep spinning until account data and the date-range totals have loaded.
  // Rendering before then would show a misleading $0 while accounts/totals are
  // still in flight. (MEW-3538)
  if (!isInitialized || !isAccountDataLoaded || !dateRangeTotalsLoaded) {
    return <Loader />
  }

  return (
    <MiniWidgetContainer
      className="mx-exp-spending-miniwidget"
      onPrimaryCtaClick={onPrimaryCtaClick}
      primaryCtaLabel={spendingCopy.primary_cta}
      primaryCtaLabelAria={spendingCopy.primary_cta_aria}
      sx={{ height: '100%', ...sx }}
      title={spendingCopy.mini_title}
    >
      <MiniSpendingChart />
    </MiniWidgetContainer>
  )
}

export default observer(SpendingMiniWidget)
